import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { stringify } from 'query-string';
import { put, select, takeEvery } from 'redux-saga/effects';

import type { RootState } from '@app/types';
import track, { AnalyticsEventName } from '@app/utils/analytics';
import { getCookie } from '@app/utils/request';

import { login, resetPassword, resetPasswordRequest, signup, ssoLogin, twoFactorLogin } from './api';
import type { ResetModalData } from './pages/LoginPage/LinkSentModal';
import { loginActions } from './pages/LoginPage/reducers';
import { resetPasswordActions } from './pages/ResetPage/reducers';
import { ssoActions } from './pages/SSOIndexPage/reducers';
import { twoFactorLoginActions } from './pages/TwoFactorPage';
import { signupActions } from './router/SignupRouter';
import * as selectors from './selectors';
import type { ResponseRedirect } from './types';

function* loginWatcher({ payload: { from } }: ReturnType<(typeof loginActions)['login']>) {
    try {
        yield put(loginActions.setLoading(true));
        const state: RootState = yield select();

        const loginFormData = selectors.getLoginFormData(state);

        const formData = {
            ...loginFormData,
            redirectTo: from,
        };

        const response: AxiosResponse<ResponseRedirect> = yield login(formData);
        track(AnalyticsEventName.SIGNED_IN, {
            type: 'email',
        });
        window.location.assign(response.data.redirect);
    } catch (err) {
        console.error(err);

        if (axios.isAxiosError(err)) {
            yield put(loginActions.setError(err.response?.data?.error));
        }
    } finally {
        yield put(loginActions.setLoading(false));
    }
}

function networkLoginWatcher({ payload: { network, from } }: ReturnType<(typeof loginActions)['networkLogin']>) {
    window.location.assign(`/login/social?${stringify({ network, from })}`);
}

function* signupWatcher() {
    try {
        yield put(signupActions.setLoading(true));
        const state: RootState = yield select();
        const formData = selectors.getSignupFormData(state);
        yield signup(formData);
        track(AnalyticsEventName.REGISTRATION_FORM_COMPLETED, {
            fullname: formData.fullname,
            email: formData.email,
            team_invite: 0,
        });
        window.location.assign('/projects');
    } catch (err) {
        if (axios.isAxiosError(err)) {
            yield put(signupActions.setPolicy(err.response?.data?.policy));
            yield put(signupActions.setError(err.response?.data?.error));
        }
    } finally {
        yield put(signupActions.setLoading(false));
    }
}

function networkSignupWatcher({
    payload: { network, projectId, lang, providerId, utmCampaign, utmContent, utmMedium, utmSource, utmTerm },
}: ReturnType<(typeof signupActions)['networkSignup']>) {
    window.location.assign(
        `/login/social?${stringify({
            network,
            projectId,
            lang,
            providerId,
            utmCampaign,
            utmContent,
            utmMedium,
            utmSource,
            utmTerm,
        })}`,
    );
}

function* resetPasswordWatcher({
    payload: { teamInvite, user },
}: ReturnType<(typeof resetPasswordActions)['resetPassword']>) {
    try {
        yield put(resetPasswordActions.setLoading(true));
        yield put(resetPasswordActions.setError(null));
        const state: RootState = yield select();
        const formData = selectors.getResetFormData(state);
        const response: AxiosResponse<ResponseRedirect> = yield resetPassword(formData);

        if (teamInvite) {
            track(AnalyticsEventName.REGISTRATION_FORM_COMPLETED, {
                fullname: user?.fullname,
                email: user?.email,
                team_invite: 1,
            });

            window.location.assign('/auth/welcome/workflow');

            return;
        }

        window.location.assign(response.data.redirect);
    } catch (err) {
        if (axios.isAxiosError(err)) {
            yield put(resetPasswordActions.setError(err.response?.data?.error));
            yield put(resetPasswordActions.setPolicy(err.response?.data?.policy));
        }
    } finally {
        yield put(resetPasswordActions.setLoading(false));
    }
}

function* twoFactorLoginWatcher() {
    try {
        yield put(twoFactorLoginActions.setLoading(true));
        yield put(twoFactorLoginActions.setError(null));
        const state: RootState = yield select();
        const formData = selectors.getTwoFactorFormData(state);
        const response: AxiosResponse<ResponseRedirect> = yield twoFactorLogin(formData);
        window.location.assign(response.data.redirect);
    } catch (err) {
        if (axios.isAxiosError(err)) {
            yield put(twoFactorLoginActions.setError(err.response?.data?.error));
        }
    } finally {
        yield put(twoFactorLoginActions.setLoading(false));
    }
}

function ssoLoginPostForm(domain: string) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `/sso/${domain}/login`;
    const token = getCookie('csrf_token');
    if (token) {
        const element = document.createElement('INPUT');
        element.setAttribute('type', 'hidden');
        element.setAttribute('name', '_X_CSRF_TOKEN');
        element.setAttribute('value', token);
        form.appendChild(element);
    }
    document.body.appendChild(form);
    form.submit();
}

function* ssoLoginWatcher({ payload: { domain } }: ReturnType<(typeof ssoActions)['ssoLogin']>) {
    try {
        yield put(ssoActions.setLoading(true));
        yield ssoLogin(domain);
        track(AnalyticsEventName.SIGNED_IN, {
            type: 'sso',
        });
        ssoLoginPostForm(domain);
    } catch (err) {
        if (axios.isAxiosError(err)) {
            yield put(ssoActions.setError(err.response?.data?.error));
        }
    } finally {
        yield put(ssoActions.setLoading(false));
    }
}

function* forgetPasswordWatcher({
    payload: { email, callback },
}: ReturnType<(typeof loginActions)['forgetPasswordRequest']>) {
    try {
        yield put(loginActions.setErrorForgetModal(null));
        yield put(loginActions.setLoadingForgetModal(true));
        const response: AxiosResponse<ResetModalData> = yield resetPasswordRequest(email);
        callback(response);
    } catch (err) {
        if (axios.isAxiosError(err)) {
            yield put(loginActions.setErrorForgetModal(err.response?.data?.error));
        }
    } finally {
        yield put(loginActions.setLoadingForgetModal(false));
    }
}

export default function* initSnapshotsSagas() {
    yield takeEvery(loginActions.login.match, loginWatcher);
    yield takeEvery(loginActions.networkLogin.match, networkLoginWatcher);
    yield takeEvery(signupActions.signup.match, signupWatcher);
    yield takeEvery(signupActions.networkSignup.match, networkSignupWatcher);
    yield takeEvery(resetPasswordActions.resetPassword.match, resetPasswordWatcher);
    yield takeEvery(twoFactorLoginActions.twoFactorLogin.match, twoFactorLoginWatcher);
    yield takeEvery(ssoActions.ssoLogin.match, ssoLoginWatcher);
    yield takeEvery(loginActions.forgetPasswordRequest.match, forgetPasswordWatcher);
}
